import { type JSX, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import { Checkbox } from 'Components/Checkbox/Checkbox';
import { Text } from 'Components/Text/Text';
import { UploadedFile } from 'Models/Files';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';
import { TprmOrUcrmDueDiligenceAssessment } from 'Models/UcrmTprm';

import styles from './AssessmentFileReuseSelector.module.css';

/**
 * The pool of files, gathered from across an assessment, that a user may reuse.
 * @param questionFiles Files uploaded when answering questions (by the third party in TPRM, or by respondents in UCRM).
 * @param reusableInternalFiles Files internal users previously attached to controls, the Review, or as assessment-wide files.
 */
export interface ReusableAssessmentFiles {
    questionFiles: UploadedFile[];
    reusableInternalFiles: UploadedFile[];
}

/**
 * Splits every already-uploaded file in an assessment into two deduplicated, separately-displayed groups:
 * * Question files (third-party files in TPRM, respondent files in UCRM): gathered from every question across the assessment. Built first so that a file originating from a question is always categorized as a question file, even if it has since been reused elsewhere.
 * * Internal files: attached by internal users to controls, the Review, or the assessment-wide files, excluding any already counted as question files.
 */
export const getReusableAssessmentFiles = (assessment: TprmOrUcrmDueDiligenceAssessment): ReusableAssessmentFiles => {
    const questionFiles: UploadedFile[] = [];
    const reusableInternalFiles: UploadedFile[] = [];
    const seenIds = new Set<string>();

    assessment.control_frameworks.forEach((framework) => {
        framework.control_groups.forEach((group) => {
            group.controls.forEach((control) => {
                control.questions.forEach((question) => {
                    question.files.forEach((file) => {
                        if (!seenIds.has(file.file_id)) {
                            seenIds.add(file.file_id);
                            questionFiles.push(file);
                        }
                    });
                });
            });
        });
    });

    assessment.control_frameworks.forEach((framework) => {
        framework.control_groups.forEach((group) => {
            group.controls.forEach((control) => {
                control.control_assessment_human.files.forEach((file) => {
                    if (!seenIds.has(file.file_id)) {
                        seenIds.add(file.file_id);
                        reusableInternalFiles.push(file);
                    }
                });
            });
        });
    });

    assessment.assessment_review_human.files.forEach((file) => {
        if (!seenIds.has(file.file_id)) {
            seenIds.add(file.file_id);
            reusableInternalFiles.push(file);
        }
    });

    assessment.assessment_wide_files.forEach((file) => {
        if (!seenIds.has(file.file_id)) {
            seenIds.add(file.file_id);
            reusableInternalFiles.push(file);
        }
    });

    return { questionFiles, reusableInternalFiles };
};

/** The props for a single reusable-file checkbox, derived from the current selection state. */
export interface FileCheckboxDescriptor {
    checked: boolean;
    name: string;
    onToggled: (isChecked: boolean) => void;
}

export interface UseAssessmentFileReuseSelectionParams {
    assessment: TprmOrUcrmDueDiligenceAssessment;
    initialSelectedUploadedFiles: UploadedFile[];

    /** New files that were dropped elsewhere (e.g., on sibling controls in the same group) but not yet uploaded. Used only to deduplicate freshly-dropped files. Defaults to an empty list for single-area consumers, such as the assessment-wide files modal. */
    existingPendingFiles?: File[];
    initialSelectedPendingFiles?: File[];
}

export interface AssessmentFileReuseSelection {
    internalFileCheckboxes: FileCheckboxDescriptor[];
    newPendingFiles: File[];
    onDropFiles: (files: File[]) => void;
    reusableFiles: ReusableAssessmentFiles;
    selectedPendingFiles: File[];
    selectedUploadedFiles: UploadedFile[];
    questionFileCheckboxes: FileCheckboxDescriptor[];
}

/**
 * Owns the selection state shared by the "Manage Files" modals of a due diligence assessment: which already-uploaded files are reused (by `file_id`) and which newly-dropped files will be uploaded on save.
 * Pair with `AssessmentFileReuseSelector` to render the selection UI, and read `newPendingFiles`/`selectedPendingFiles`/`selectedUploadedFiles` when the consuming modal saves.
 */
export const useAssessmentFileReuseSelection = (params: UseAssessmentFileReuseSelectionParams): AssessmentFileReuseSelection => {
    const existingPendingFiles = params.existingPendingFiles ?? [];

    // Files that the user drags and drops that haven't already been used elsewhere in the assessment. These will be uploaded when the modal's changes are saved (as long as they are still selected at that time).
    const [newPendingFiles, setNewPendingFiles] = useState<File[]>([]);
    const [selectedPendingFiles, setSelectedPendingFiles] = useState(params.initialSelectedPendingFiles ?? []);
    const [selectedUploadedFiles, setSelectedUploadedFiles] = useState(params.initialSelectedUploadedFiles);

    const reusableFiles = useMemo(() => getReusableAssessmentFiles(params.assessment), [params.assessment]);
    const { questionFiles, reusableInternalFiles } = reusableFiles;

    const onDropFiles = (files: File[]) => {
        for (const file of files) {
            // Deduplicate dropped files only against the pool of internal files. A question file that happens to share the same filename as an internal file is a different file.
            const duplicateUploadedFile = reusableInternalFiles.find((uploadedFile) => uploadedFile.filename === file.name);
            if (duplicateUploadedFile) {
                if (!selectedUploadedFiles.includes(duplicateUploadedFile)) {
                    setSelectedUploadedFiles((current) => [...current, duplicateUploadedFile]);
                }
            } else {
                const duplicateExistingPendingFile = existingPendingFiles.find((pendingFile) => pendingFile.name === file.name);
                const duplicateNewPendingFile = newPendingFiles.find((pendingFile) => pendingFile.name === file.name);

                if (!duplicateExistingPendingFile && !duplicateNewPendingFile) {
                    setNewPendingFiles((current) => [...current, file]);
                    setSelectedPendingFiles((current) => [...current, file]);
                } else if (duplicateNewPendingFile && !selectedPendingFiles.includes(file)) {
                    setSelectedPendingFiles((current) => [...current, file]);
                }
            }
        }
    };

    /** Maps an existing uploaded file (internal or from a question) to the props for its checkbox. Both sections select into the same `selectedUploadedFiles` state, keyed by `file_id`. */
    const uploadedFileToCheckboxValue = (uploadedFile: UploadedFile): FileCheckboxDescriptor => ({
        name: uploadedFile.filename,
        checked: selectedUploadedFiles.some((f) => f.file_id === uploadedFile.file_id),
        onToggled: (isChecked: boolean) => {
            if (isChecked) {
                setSelectedUploadedFiles((current) => [...current, uploadedFile]);
            } else {
                setSelectedUploadedFiles((current) => current.filter((f) => f.file_id !== uploadedFile.file_id));
            }
        },
    });

    const internalFileCheckboxes: FileCheckboxDescriptor[] = (() => {
        const existingFileValues = reusableInternalFiles.map(uploadedFileToCheckboxValue);

        const pendingFileValues = [...existingPendingFiles, ...newPendingFiles].map((f) => ({
            name: f.name,
            checked: selectedPendingFiles.map((spf) => spf.name).includes(f.name),
            onToggled: (isChecked: boolean) => {
                if (isChecked) {
                    setSelectedPendingFiles((current) => [...current, f]);
                } else {
                    setSelectedPendingFiles((current) => current.filter((currentFile) => currentFile.name !== f.name));
                }
            },
        }));

        return [...existingFileValues, ...pendingFileValues].sort((a, b) => a.name.localeCompare(b.name));
    })();

    const questionFileCheckboxes: FileCheckboxDescriptor[] = questionFiles.map(uploadedFileToCheckboxValue).sort((a, b) => a.name.localeCompare(b.name));

    return { internalFileCheckboxes, newPendingFiles, onDropFiles, reusableFiles, selectedPendingFiles, selectedUploadedFiles, questionFileCheckboxes };
};

export interface AssessmentFileReuseSelectorProps {
    internalFileCheckboxes: FileCheckboxDescriptor[];
    module: DueDiligenceModule;
    onDropFiles: (files: File[]) => void;
    questionFileCheckboxes: FileCheckboxDescriptor[];

    disabled?: boolean;
}

/**
 * Presentational selection UI shared by the "Manage Files" modals of a due diligence assessment: a drop area for new files plus deduplicated "Internal files" and "Third-party files" checkbox sections.
 * Drive it with `useAssessmentFileReuseSelection`, which owns the selection state behind the checkbox descriptors and the drop handler.
 */
export const AssessmentFileReuseSelector = (props: AssessmentFileReuseSelectorProps): JSX.Element => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: props.onDropFiles, multiple: true });
    const dragClassName = isDragActive ? styles.dropzoneDragActive : '';

    return (
        <div className={styles.selector}>
            <div {...getRootProps({ className: `${styles.dropzone} ${dragClassName}` })}>
                <input disabled={props.disabled} id="fileArea" {...getInputProps()} />
                <Text color="darkGray">To add new files, click here or drag and drop here.</Text>
            </div>
            {props.internalFileCheckboxes.length > 0 && (
                <div className={styles.section}>
                    <Text variant="Header4" noStyles>
                        Internal files
                    </Text>
                    {props.internalFileCheckboxes.map((v) => (
                        <Checkbox key={v.name} disabled={props.disabled} checked={v.checked} label={v.name} onToggled={v.onToggled} />
                    ))}
                </div>
            )}
            {props.questionFileCheckboxes.length > 0 && (
                <div className={styles.section}>
                    <Text variant="Header4" noStyles>
                        {props.module === ProductModule.UCRM ? 'Respondent files' : 'Third-party files'}
                    </Text>
                    {props.questionFileCheckboxes.map((v) => (
                        <Checkbox key={v.name} disabled={props.disabled} checked={v.checked} label={v.name} onToggled={v.onToggled} />
                    ))}
                </div>
            )}
        </div>
    );
};
